import React, { Component } from 'react';
/**
 * macOS-style Dock Component
 * Shows app icons with smart indicators and proper minimize/focus behavior
 */

import { motion } from 'framer-motion';
import { Settings as SettingsIcon } from 'lucide-react';
import { useTheme } from '../../core/contexts/ThemeContext';
import { useWindows } from '../../core/contexts/WindowContext';
import { useAppRegistry } from '../../core/contexts/AppRegistry';
export function Dock() {
  const {
    getThemeClasses,
    t
  } = useTheme();
  const {
    windows,
    openWindow,
    focusWindow,
    minimizeWindow,
    isMobile
  } = useWindows();
  const {
    apps
  } = useAppRegistry();
  const theme = getThemeClasses();
  // Filter apps
  const mainApps = apps.filter(app => app.category === 'main');
  const settingsApp = apps.find(app => app.id === 'settings');
  const handleAppClick = (appId: string, appTitle: string) => {
    const appWindows = windows.filter(w => w.appId === appId);
    if (appWindows.length === 0) {
      // No window exists - open new one
      openWindow(appId, appTitle);
      return;
    }
    // Find window with highest zIndex (most recently focused)
    const activeWindow = appWindows.reduce((prev, current) => current.zIndex > prev.zIndex ? current : prev);
    if (activeWindow.isMinimized) {
      // Restore minimized window
      minimizeWindow(activeWindow.id);
      return;
    }
    // Check if this window is the top window across ALL windows
    const maxZIndex = Math.max(...windows.map(w => w.zIndex));
    const isTopWindow = activeWindow.zIndex === maxZIndex;
    if (isTopWindow) {
      // Already on top - minimize it
      minimizeWindow(activeWindow.id);
    } else {
      // Not on top - focus it
      focusWindow(activeWindow.id);
    }
  };
  // Count visible (non-minimized) windows per app
  const getWindowCount = (appId: string) => {
    return windows.filter(w => w.appId === appId && !w.isMinimized).length;
  };
  // Mobile: Bottom bar
  if (isMobile) {
    return <motion.div initial={{
      y: 100,
      opacity: 0
    }} animate={{
      y: 0,
      opacity: 1
    }} transition={{
      delay: 0.5
    }} className={`
          fixed bottom-0 left-0 right-0
          ${theme.card}
          border-t ${theme.text}
          px-2 py-2
          flex items-center justify-between
          z-[9999]
        `} data-id="element-882">
        <div className="flex items-center gap-1 flex-1 justify-around" data-id="element-883">
          {mainApps.map(app => {
          const windowCount = getWindowCount(app.id);
          const Icon = app.icon;
          return <button key={app.id} onClick={() => handleAppClick(app.id, t(`${app.id}.title`))} className={`
                  relative p-2 rounded-xl
                  bg-gradient-to-br ${app.color}
                  flex items-center justify-center
                  transition-all
                  ${windowCount > 0 ? 'ring-2 ring-white/40 scale-110' : 'scale-100'}
                `} title={t(`${app.id}.title`)} data-id="element-884">
                <Icon className="w-5 h-5 text-white" data-id="element-885" />
                {windowCount > 0 && <>
                    <div className="absolute -bottom-1 w-1 h-1 rounded-full bg-white" data-id="element-886" />
                    {windowCount > 1 && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-fuchsia-500 text-white text-xs flex items-center justify-center font-bold" data-id="element-887">
                        {windowCount}
                      </div>}
                  </>}
              </button>;
        })}
        </div>
        {settingsApp && <button onClick={() => handleAppClick('settings', 'Settings')} className={`
              p-2 rounded-xl ml-2
              ${theme.card}
              border border-white/10
              hover:scale-110 transition-transform
            `} title="Settings" data-id="element-888">
            <SettingsIcon className="w-5 h-5 opacity-70" data-id="element-889" />
          </button>}
      </motion.div>;
  }
  // Desktop: Floating dock
  return <motion.div initial={{
    y: 100,
    opacity: 0
  }} animate={{
    y: 0,
    opacity: 1
  }} transition={{
    delay: 0.5
  }} className="fixed bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3 z-[9999]" data-id="element-890">
      <div className={`${theme.card} ${theme.shadow} rounded-2xl px-3 py-2 flex items-center gap-2`} data-id="element-891">
        {mainApps.map(app => {
        const windowCount = getWindowCount(app.id);
        const Icon = app.icon;
        return <motion.button key={app.id} onClick={() => handleAppClick(app.id, t(`${app.id}.title`))} whileHover={{
          scale: 1.2,
          y: -8
        }} whileTap={{
          scale: 0.95
        }} className={`
                relative w-12 h-12 rounded-xl
                bg-gradient-to-br ${app.color}
                flex items-center justify-center
                transition-all
                ${windowCount > 0 ? 'ring-2 ring-white/40' : ''}
              `} title={t(`${app.id}.title`)} data-id="element-892">
              <Icon className="w-6 h-6 text-white" data-id="element-893" />
              {windowCount > 0 && <>
                  <motion.div layoutId={`dock-indicator-${app.id}`} className="absolute -bottom-1 w-1 h-1 rounded-full bg-white" data-id="element-894" />
                  {windowCount > 1 && <motion.div initial={{
              scale: 0
            }} animate={{
              scale: 1
            }} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-fuchsia-500 text-white text-xs flex items-center justify-center font-bold shadow-lg" data-id="element-895">
                      {windowCount}
                    </motion.div>}
                </>}
            </motion.button>;
      })}
      </div>
      {settingsApp && <motion.button onClick={() => handleAppClick('settings', 'Settings')} whileHover={{
      scale: 1.1,
      rotate: 45
    }} whileTap={{
      scale: 0.95
    }} className={`
            ${theme.card} ${theme.shadow}
            w-12 h-12 rounded-xl
            flex items-center justify-center
          `} title="Settings" data-id="element-896">
          <SettingsIcon className="w-6 h-6 opacity-70" data-id="element-897" />
        </motion.button>}
    </motion.div>;
}